import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";

const FIELDS = ["name","specialty","country","telephone","address","website","intro","biography","introAr","biographyAr"];
const MAX_WORKS = 4;

export default function EditProfile() {
  const { t, lang } = useApp();
  const navigate = useNavigate();

  const [form, setForm]           = useState(null);
  const [profile, setProfile]     = useState(null);   // new profile photo File
  const [keptUrls, setKeptUrls]   = useState([]);     // existing artwork URLs to keep
  const [newFiles, setNewFiles]   = useState([]);     // new artwork Files to add
  const [busy, setBusy]           = useState(false);
  const [msg, setMsg]             = useState("");
  const [error, setError]         = useState("");
  const addRef = useRef();

  const token = localStorage.getItem("spar_token");

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    const user = JSON.parse(localStorage.getItem("spar_user") || "null");
    if (!user) { navigate("/login"); return; }
    axios.get(`/api/artists/${user._id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const a = r.data.artist || r.data;
        setForm({
          name: a.name || "", specialty: a.specialty || "",
          country: a.country || "", telephone: a.telephone || "",
          address: a.address || "", website: a.website || "",
          intro: a.intro || "", biography: a.biography || "",
          introAr: a.introAr || "", biographyAr: a.biographyAr || "",
          _id: a._id, _currentPhoto: a.profileImage || "",
        });
        setKeptUrls(a.images || []);
      })
      .catch(() => setError("Could not load your profile."));
  }, [token, navigate]);

  const onChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const removeExisting = (url) => setKeptUrls(keptUrls.filter(u => u !== url));
  const removeNew = (idx) => setNewFiles(newFiles.filter((_, i) => i !== idx));

  const onAddFiles = e => {
    const slots = MAX_WORKS - keptUrls.length - newFiles.length;
    if (slots <= 0) return;
    const picked = Array.from(e.target.files).slice(0, slots);
    setNewFiles([...newFiles, ...picked]);
    e.target.value = "";
  };

  const onSubmit = async e => {
    e.preventDefault(); setError(""); setMsg(""); setBusy(true);
    try {
      const data = new FormData();
      FIELDS.forEach(k => data.append(k, form[k] || ""));
      if (profile) data.append("profileImage", profile);
      keptUrls.forEach(url => data.append("keepImages", url));
      newFiles.forEach(f => data.append("images", f));
      const res = await axios.put("/api/artists/me", data, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      localStorage.setItem("spar_user", JSON.stringify(res.data.artist));
      setMsg(t("profileUpdated") || "Profile updated successfully.");
      setNewFiles([]);
      setKeptUrls(res.data.artist.images || []);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.response?.data?.error || "Update failed.");
    } finally { setBusy(false); }
  };

  if (!form) return <div className="section container empty-state">{error || t("loading")}</div>;

  const totalSlots = keptUrls.length + newFiles.length;
  const canAdd = totalSlots < MAX_WORKS;

  return (
    <>
      <div className="page-band">
        <div className="container" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
          <div>
            <h1>{t("editProfile") || "Edit Profile"}</h1>
            <p>{t("editProfileSub") || "Update your artist information."}</p>
          </div>
          {form._id && (
            <a
              href={`/artists/${form._id}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-outline"
              style={{ whiteSpace:"nowrap" }}
            >
              {lang === "ar" ? "معاينة ملفي الشخصي" : "Preview my profile"}
            </a>
          )}
        </div>
      </div>

      <section className="section container">
        <form className="form-wrap card-panel" onSubmit={onSubmit}>
          {msg   && <div className="alert alert-success">{msg}</div>}
          {error && <div className="alert alert-error">{error}</div>}

          <div className="grid-2">
            <div className="field"><label>{t("fullName")} <span className="req">*</span></label><input className="input" name="name" value={form.name} onChange={onChange} required /></div>
            <div className="field"><label>{t("specialty")} <span className="req">*</span></label><input className="input" name="specialty" value={form.specialty} onChange={onChange} required /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>{t("country")}</label><input className="input" name="country" value={form.country} onChange={onChange} /></div>
            <div className="field"><label>{t("telephone")}</label><input className="input" name="telephone" value={form.telephone} onChange={onChange} /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>{t("address")}</label><input className="input" name="address" value={form.address} onChange={onChange} /></div>
            <div className="field"><label>{t("website")}</label><input className="input" name="website" value={form.website} onChange={onChange} placeholder="https://" /></div>
          </div>

          <div className="field">
            <label>{t("shortIntro")} <span className="req">*</span></label>
            <textarea className="textarea" name="intro" value={form.intro} onChange={onChange} placeholder={t("introPh")} required />
          </div>
          <div className="field">
            <label>{t("biography")} <span className="req">*</span></label>
            <textarea className="textarea" name="biography" value={form.biography} onChange={onChange} style={{ minHeight:130 }} placeholder={t("bioPh")} required />
          </div>

          <div className="card-panel" style={{ background:"var(--cream)", borderRadius:10, padding:"20px 22px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <i className="fa-solid fa-language" style={{ color:"var(--brand)" }} />
              <span style={{ fontWeight:700 }}>{lang==="ar" ? "الحقول العربية" : "Arabic Fields"}</span>
            </div>
            <div className="field"><label dir="rtl">{t("shortIntroAr")}</label><textarea className="textarea" name="introAr" value={form.introAr} onChange={onChange} dir="rtl" style={{ fontFamily:"Cairo, sans-serif" }} placeholder={t("introArPh")} /></div>
            <div className="field"><label dir="rtl">{t("biographyAr")}</label><textarea className="textarea" name="biographyAr" value={form.biographyAr} onChange={onChange} dir="rtl" style={{ fontFamily:"Cairo, sans-serif", minHeight:130 }} placeholder={t("bioArPh")} /></div>
          </div>

          {/* Profile photo */}
          <div className="field">
            <label>{t("profilePhoto")}</label>
            <div style={{ display:"flex", alignItems:"flex-end", gap:14, flexWrap:"wrap", marginTop:6 }}>
              {(profile ? URL.createObjectURL(profile) : form._currentPhoto) && (
                <div style={{ position:"relative", display:"inline-block" }}>
                  <img
                    src={profile ? URL.createObjectURL(profile) : form._currentPhoto}
                    alt="profile"
                    style={{ width:80, height:80, objectFit:"cover", borderRadius:10, border:"2px solid var(--border,#ddd)" }}
                  />
                  {profile && (
                    <button type="button" onClick={() => setProfile(null)} style={removeStyle}>×</button>
                  )}
                </div>
              )}
              <label className="btn btn-outline" style={{ cursor:"pointer" }}>
                {lang==="ar" ? "تغيير الصورة" : "Change photo"}
                <input type="file" accept="image/*" style={{ display:"none" }} onChange={e => setProfile(e.target.files[0]||null)} />
              </label>
            </div>
          </div>

          {/* Artworks */}
          <div className="field">
            <label style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span>{t("workSamples") || "Artwork samples"}</span>
              <span style={{ fontSize:"0.8rem", fontWeight:400, color:"var(--muted,#888)" }}>
                {totalSlots}/{MAX_WORKS}
              </span>
            </label>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginTop:10 }}>
              {/* Existing kept images */}
              {keptUrls.map((url, i) => (
                <div key={`k-${i}`} style={{ position:"relative" }}>
                  <img src={url} alt={`work ${i+1}`} style={thumbStyle} />
                  <button type="button" onClick={() => removeExisting(url)} style={removeStyle} title="Remove">×</button>
                </div>
              ))}

              {/* New files preview */}
              {newFiles.map((f, i) => (
                <div key={`n-${i}`} style={{ position:"relative" }}>
                  <img src={URL.createObjectURL(f)} alt={`new ${i+1}`} style={{ ...thumbStyle, outline:"2px solid var(--brand,#c0a060)" }} />
                  <button type="button" onClick={() => removeNew(i)} style={removeStyle} title="Remove">×</button>
                  <span style={newBadge}>new</span>
                </div>
              ))}

              {/* Add slot */}
              {canAdd && (
                <label style={addSlotStyle} title={lang==="ar" ? "إضافة صورة" : "Add artwork"}>
                  <span style={{ fontSize:"1.6rem", lineHeight:1, color:"var(--muted,#aaa)" }}>+</span>
                  <span style={{ fontSize:"0.7rem", color:"var(--muted,#aaa)", marginTop:4 }}>
                    {lang==="ar" ? "إضافة" : "Add"}
                  </span>
                  <input ref={addRef} type="file" accept="image/*" multiple style={{ display:"none" }} onChange={onAddFiles} />
                </label>
              )}
            </div>

            {totalSlots === 0 && (
              <p style={{ fontSize:"0.82rem", color:"var(--muted,#888)", marginTop:6 }}>
                {lang==="ar"
                  ? "أضف 3 أعمال على الأقل لكي تظهر بطاقتك للزوار."
                  : "Add at least 3 artworks for your card to appear on the Artists page."}
              </p>
            )}
          </div>

          <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={busy}>
            {busy ? (t("submitting") || "Saving…") : (t("saveChanges") || "Save Changes")}
          </button>
        </form>
      </section>
    </>
  );
}

const thumbStyle = {
  width:"100%", aspectRatio:"1/1", objectFit:"cover",
  borderRadius:8, border:"1px solid var(--border,#ddd)", display:"block",
};

const removeStyle = {
  position:"absolute", top:4, right:4,
  background:"rgba(0,0,0,0.65)", color:"#fff",
  border:"none", borderRadius:"50%",
  width:22, height:22, fontSize:"0.9rem", lineHeight:1,
  cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
  padding:0,
};

const newBadge = {
  position:"absolute", bottom:4, left:4,
  background:"var(--brand,#c0a060)", color:"#fff",
  fontSize:"0.6rem", fontWeight:700, padding:"2px 6px", borderRadius:4,
  textTransform:"uppercase",
};

const addSlotStyle = {
  aspectRatio:"1/1", border:"2px dashed var(--border,#ddd)",
  borderRadius:8, display:"flex", flexDirection:"column",
  alignItems:"center", justifyContent:"center",
  cursor:"pointer", transition:"border-color 0.15s",
};
